import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Button, Input, Text, Card, Avatar } from '@teals/ui-web';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks';

const PageContainer = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StrengthBarTrack = styled.div`
  width: 100%;
  height: 6px;
  background: var(--bg-inset, #e2e8f0);
  border-radius: 3px;
  overflow: hidden;
`;

const StrengthBarFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  border-radius: 3px;
  transition: width 0.3s, background 0.3s;
`;

const StrengthLabel = styled.span<{ $color: string }>`
  font-size: 0.75rem;
  color: ${({ $color }) => $color};
  margin-top: 0.25rem;
`;

const StrengthWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SignOutSection = styled.div`
  display: flex;
  justify-content: flex-end;
`;

function getPasswordStrength(password: string): {
  width: number;
  color: string;
  label: string;
} {
  if (password.length === 0) return { width: 0, color: 'transparent', label: '' };
  if (password.length < 6) return { width: 25, color: '#dc2626', label: 'Weak' };
  if (password.length < 10) return { width: 50, color: '#ea580c', label: 'Fair' };
  if (password.length < 14) return { width: 75, color: '#eab308', label: 'Good' };
  return { width: 100, color: '#16a34a', label: 'Strong' };
}

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(user?.name ?? 'Alex Morgan');
  const mockEmail = user?.email ?? 'alex.morgan@company.com';

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  const handleSignOut = () => {
    logout();
    navigate('/auth');
  };

  return (
    <PageContainer>
      <Text variant="h2">Profile</Text>

      <SectionCard padding={24}>
        <AvatarSection>
          <Avatar name={displayName} size="lg" />
          <Button variant="outline" size="sm">
            Upload Photo
          </Button>
        </AvatarSection>
        <FieldRow>
          <Input
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <Input label="Email" value={mockEmail} disabled />
        </FieldRow>
      </SectionCard>

      <SectionCard padding={24}>
        <Text variant="h4">Change Password</Text>
        <FieldRow>
          <Input
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
          />
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
          />
          <StrengthWrapper>
            <StrengthBarTrack>
              <StrengthBarFill $width={strength.width} $color={strength.color} />
            </StrengthBarTrack>
            {strength.label && (
              <StrengthLabel $color={strength.color}>{strength.label}</StrengthLabel>
            )}
          </StrengthWrapper>
          <Input
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
          />
        </FieldRow>
        <Button variant="primary">Update Password</Button>
      </SectionCard>

      <SignOutSection>
        <Button variant="danger" onClick={handleSignOut}>
          Sign Out
        </Button>
      </SignOutSection>
    </PageContainer>
  );
};

export default Profile;
